// src/components/SystemSelectionDialog.js

import React, { useState } from 'react';
import {
    Button,
    Checkbox,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    FormControlLabel,
    FormGroup,
    Stack,
    Switch,
    Typography,
    Box
} from '@mui/material';

// Import Icons
import ElectricalServicesIcon from '@mui/icons-material/ElectricalServices';
import WaterIcon from '@mui/icons-material/Water';
import LocalHospitalIcon from '@mui/icons-material/LocalHospital';
import LocalPharmacyIcon from '@mui/icons-material/LocalPharmacy';
import AccountBalanceIcon from '@mui/icons-material/AccountBalance';
import SchoolIcon from '@mui/icons-material/School';
import LocalPoliceIcon from '@mui/icons-material/LocalPolice';
import FireTruckIcon from '@mui/icons-material/FireTruck';
import MilitaryTechIcon from '@mui/icons-material/MilitaryTech';
import DirectionsTransitIcon from '@mui/icons-material/DirectionsTransit';
import StorefrontIcon from '@mui/icons-material/Storefront';

// System definitions
const ALL_SYSTEMS = [
    { key: 'power', label: 'Power', icon: <ElectricalServicesIcon />, category: 'utility' },
    { key: 'water', label: 'Water', icon: <WaterIcon />, category: 'utility' },
    { key: 'health', label: 'Healthcare', icon: <LocalHospitalIcon />, category: 'social' },
    { key: 'police', label: 'Police', icon: <LocalPoliceIcon />, category: 'social' },
    { key: 'fire', label: 'Fire Stations', icon: <FireTruckIcon />, category: 'social' },
    { key: 'military', label: 'Military', icon: <MilitaryTechIcon />, category: 'social' },
    { key: 'pharmacy', label: 'Pharmacies', icon: <LocalPharmacyIcon />, category: 'social' },
    { key: 'food', label: 'Food Retail', icon: <StorefrontIcon />, category: 'social' },
    { key: 'train', label: 'Train Stations', icon: <DirectionsTransitIcon />, category: 'social' },
    { key: 'school', label: 'Schools', icon: <SchoolIcon />, category: 'social' },
    { key: 'office', label: 'Government Offices', icon: <AccountBalanceIcon />, category: 'social' },
];

const SOCIAL_SYSTEMS_KEYS = ALL_SYSTEMS.filter(s => s.category === 'social').map(s => s.key);

const SystemSelectionDialog = ({ open, onClose, onConfirm }) => {
    const [selected, setSelected] = useState({});
    // --- NEW: State for the cascade toggle ---
    const [cascadeEnabled, setCascadeEnabled] = useState(true);

    const handleChange = (event) => {
        const { name, checked } = event.target;
        let newSelected = { ...selected, [name]: checked };

        // --- MODIFIED: Only apply dependency logic if cascading is enabled ---
        if (cascadeEnabled) {
            if (SOCIAL_SYSTEMS_KEYS.includes(name) && checked) {
                newSelected.power = true;
                newSelected.water = true;
            }
            if (name === 'water' && checked) {
                newSelected.power = true;
            }
            if (name === 'power' && !checked) {
                newSelected.water = false;
                SOCIAL_SYSTEMS_KEYS.forEach(key => { newSelected[key] = false; });
            }
            if (name === 'water' && !checked) {
                SOCIAL_SYSTEMS_KEYS.forEach(key => { newSelected[key] = false; });
            }
        }
        setSelected(newSelected);
    };

    const handleConfirm = () => {
        const selectedSystems = Object.keys(selected).filter(key => selected[key]);
        // --- MODIFIED: Pass the cascade setting back ---
        onConfirm(selectedSystems, cascadeEnabled);
    };

    return (
        <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
            <DialogTitle>Select Systems for Simulation</DialogTitle>
            <DialogContent dividers>
                <Typography variant="body2" color="text.secondary" gutterBottom>
                    Choose the infrastructure systems to include in the risk analysis.
                </Typography>
                
                {/* --- NEW: Cascade Effects Toggle Switch --- */}
                <Box sx={{ border: '1px solid', borderColor: 'divider', borderRadius: 1, p: 1.5, my: 2 }}>
                    <FormControlLabel
                        control={
                            <Switch
                                checked={cascadeEnabled}
                                onChange={(e) => setCascadeEnabled(e.target.checked)}
                            />
                        }
                        label="Enable Cascade Effects"
                    />
                    <Typography variant="caption" color="text.secondary" display="block" sx={{ pl: 1 }}>
                        {cascadeEnabled 
                            ? "Failures will cascade from utilities (Power, Water) to social services. System dependencies are enforced."
                            : "Each selected system will be simulated independently against the initial hazard only."
                        }
                    </Typography>
                </Box>

                <FormGroup>
                    {ALL_SYSTEMS.map((system) => (
                        <FormControlLabel
                            key={system.key}
                            control={
                                <Checkbox
                                    checked={!!selected[system.key]}
                                    onChange={handleChange}
                                    name={system.key}
                                    // --- MODIFIED: Disable only when cascading is on ---
                                    disabled={
                                        cascadeEnabled && (
                                            (system.key === 'power' && (selected.water || SOCIAL_SYSTEMS_KEYS.some(k => selected[k]))) ||
                                            (system.key === 'water' && SOCIAL_SYSTEMS_KEYS.some(k => selected[k]))
                                        )
                                    }
                                />
                            }
                            label={
                                <Stack direction="row" alignItems="center" spacing={1.5}>
                                    {system.icon}
                                    <Typography>{system.label}</Typography>
                                </Stack>
                            }
                        />
                    ))}
                </FormGroup>
            </DialogContent>
            <DialogActions>
                <Button onClick={onClose}>Cancel</Button>
                <Button onClick={handleConfirm} variant="contained">Run Analysis</Button>
            </DialogActions>
        </Dialog>
    );
};

export default SystemSelectionDialog;